// Image hosting utility for Modern Human Being platform
// This provides multiple image hosting strategies

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy 1: Local File Storage (Current Development)
export const uploadToLocal = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target.result); // Returns base64
    };
    reader.readAsDataURL(file);
  });
};

// Strategy 2: Cloudinary Integration (Recommended for Production)
export const uploadToCloudinary = async (file, options = {}) => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required for image upload');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', options.folder || 'mhb_uploads');
  
  try {
    const response = await fetch(`${API}/upload/cloudinary`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Upload failed');
    }
    
    const data = await response.json();
    return {
      url: data.url,
      publicId: data.public_id,
      format: data.format,
      size: data.size,
    };
  } catch (error) {
    console.error('Cloudinary upload failed:', error);
    throw error;
  }
};

// Upload base64 image to Cloudinary
export const uploadBase64ToCloudinary = async (base64Data, options = {}) => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required for image upload');
  }
  
  try {
    const response = await fetch(`${API}/upload/cloudinary-base64`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_data: base64Data,
        folder: options.folder || 'mhb_uploads'
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Upload failed');
    }
    
    const data = await response.json();
    return {
      url: data.url,
      publicId: data.public_id,
      format: data.format,
      size: data.size,
    };
  } catch (error) {
    console.error('Cloudinary base64 upload failed:', error);
    throw error;
  }
};

// Delete image from Cloudinary
export const deleteFromCloudinary = async (publicId) => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    throw new Error('Authentication required for image deletion');
  }
  
  try {
    const response = await fetch(`${API}/cloudinary/${publicId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Delete failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Cloudinary delete failed:', error);
    throw error;
  }
};

// Strategy 3: AWS S3 Integration (Enterprise Option)
export const uploadToS3 = async (file, options = {}) => {
  // This would require AWS SDK setup
  // Implementation depends on backend S3 integration
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', options.folder || 'vision-board');
  
  try {
    const response = await fetch('/api/upload/s3', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${options.token}`,
      },
      body: formData,
    });
    
    const data = await response.json();
    return {
      url: data.url,
      key: data.key,
      bucket: data.bucket,
    };
  } catch (error) {
    console.error('S3 upload failed:', error);
    throw error;
  }
};

// Image optimization utilities
export const optimizeImage = (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      
      // Draw optimized image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Convert to blob
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Create multiple sizes for different use cases
export const createImageSizes = async (file) => {
  const sizes = {
    thumbnail: await optimizeImage(file, 150, 0.7),    // Thumbnails
    medium: await optimizeImage(file, 400, 0.8),       // Medium posts
    large: await optimizeImage(file, 800, 0.8),        // Large posts
    original: await optimizeImage(file, 1200, 0.9)     // Original quality
  };
  
  return sizes;
};

// Get recommended size based on post boost level or purpose
export const getRecommendedImageSize = (boostLevel, purpose = 'post') => {
  // For vision boards, preserve maximum quality
  if (purpose === 'vision_board' || purpose === 'affirmation_image') {
    return { width: 2400, height: 2400, quality: 1.0, preserve: true };
  }
  
  // For posts, use boost level settings
  switch (boostLevel) {
    case 'standard':
      return { width: 800, height: 600, quality: 0.85 };
    case 'premium':
      return { width: 1200, height: 800, quality: 0.90 };
    case 'vip':
      return { width: 1600, height: 1200, quality: 0.95 };
    default:
      return { width: 800, height: 600, quality: 0.85 };
  }
};

// Image validation
export const validateImage = (file) => {
  const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
  const maxSize = 5 * 1024 * 1024; // 5MB
  
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid image type. Please use JPEG, PNG, WebP, or GIF.');
  }
  
  if (file.size > maxSize) {
    throw new Error('Image too large. Please use images under 5MB.');
  }
  
  return true;
};

// Main upload function that chooses strategy based on environment
export const uploadImage = async (file, strategy = 'cloudinary', options = {}) => {
  try {
    // Validate the image first
    validateImage(file);
    
    // Get recommended size if boost level is provided
    const boostLevel = options.boostLevel || 'standard';
    const purpose = options.purpose || 'post';
    const recommendedSize = getRecommendedImageSize(boostLevel, purpose);
    
    // For vision boards and affirmation images, skip frontend compression to preserve quality
    let fileToUpload = file;
    if (!recommendedSize.preserve) {
      // Only compress for regular posts
      fileToUpload = await optimizeImage(file, recommendedSize.width, recommendedSize.quality);
    }
    
    // Choose upload strategy
    switch (strategy) {
      case 'cloudinary':
        return await uploadToCloudinary(fileToUpload, { ...options, preserveQuality: recommendedSize.preserve });
      case 's3':
        return await uploadToS3(fileToUpload, options);
      case 'local':
      default:
        return await uploadToLocal(fileToUpload);
    }
  } catch (error) {
    console.error('Image upload failed:', error);
    throw error;
  }
};

// Convert base64 to file for upload
export const base64ToFile = (base64String, filename = 'image.jpg') => {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mime });
};